/**
 * RMASlaOverview — SLA catalog + live pending/overdue status for After-Sales.
 *
 * Page-scoped (not a shared component → JSDoc header only, no registry entry).
 * Two data sources, both under rma_bp:
 *   GET /api/rma/sla-definitions: the 18-row catalog (14 until N60, 16 until N65;
 *     data-driven from the SERVICE_SLAS / RETURN_SLAS / INQUIRY_SLAS engine maps;
 *     never hardcoded here so it can't drift from rma_sla.py).
 *   GET /api/rma/sla-overview — live aggregate over OPEN cases: per SLA type how
 *     many are pending / overdue (+ the overdue cases), plus a failsafe bucket of
 *     no-SLA-def cases overdue via sla_due_date, so the coverage gap is visible.
 *
 * Overdue is derived server-side (aggregate_sla_overview → reuses the #763
 * helpers); this page only renders. SLA `type` labels come from the shared
 * SLA_KIND_LABELS so they match the case timeline.
 *
 * 🔴 G13a-1 (UI2 Teil 10, Plan `2026-10-21-ui2-t10-g13a-berichte-1.md` §2.2):
 * the page is a BERICHTSfläche with three LISTS, and each of them carries its
 * own counter. What changed against the pre-G13a build, one line each:
 *
 *   · C5-1 — three single `Panel` cards became ONE `KpiRow` with TWO `StatTile`
 *     (Pending · Overdue). „SLA definitions" LEFT the KPI row and became the
 *     catalog's `ResultCount` (C5-7): the same number must not stand twice on
 *     one page (W4/K3).
 *   · C5-2 — two INDEPENDENT fetches instead of `Promise.all`. Each source has
 *     its own `loading`/`error`; a failed source shows „—" and its own
 *     `EmptyState`, the other one keeps its data. Before this, ONE `Alert`
 *     took both down.
 *   · C5-3 — the live table sorts by `overdue_count` desc, then `pending_count`
 *     desc, CLIENT-side (display only, C5-9). The server order was the insertion
 *     order of the case iteration (no `ORDER BY`), i.e. undefined.
 *   · C5-4/5 — mobile gets a reduced row (`MobileRecordCard variant="row"`);
 *     its chevron opens a `MobileSheet` with the overdue cases. Desktop keeps
 *     the inline `Collapse`.
 *   · C5-6 — the failsafe section renders ALWAYS once the overview is loaded:
 *     with cases the table, without cases an `EmptyState reason="no-data"`.
 *     `return null` made „nothing to report" and „not measured" look alike.
 *   · C5-6b — the live table got a `ResultCount`; it had none.
 *   · C5-8 — only `page` is in the URL (the failsafe list), via `useListQuery`.
 *
 * 🔴 Zonenmarken (W2/W-B): ONE outer `liste-panel` — `oeffne()` in
 * `tests/listen/helfer.ts` waits for `SEL.panel`, and three identical ids would
 * make that wait ambiguous — plus one own mark per list
 * (`liste-panel-live` / `-failsafe` / `-katalog`). `messeP8` takes the list's own
 * id through the additive `route.panel` argument and measures the counter per
 * list; without it the outer mark sees all three and reports „3 counters,
 * exactly one allowed".
 */
import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box, Typography, Stack, Chip, Alert, Link, useMediaQuery,
  Table, TableHead, TableBody, TableRow, TableCell, Collapse, IconButton,
  DialogTitle, DialogContent,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import PageContainer from '../components/common/PageContainer'
import Panel from '../components/common/Panel'
import TableViewport from '../components/common/TableViewport'
import TableFooterSimple from '../components/common/TableFooterSimple'
import ResultCount from '../components/common/ResultCount'
import EmptyState from '../components/common/EmptyState'
import KpiRow, { KpiCell } from '../components/common/KpiRow'
import StatTile from '../components/common/StatTile'
import MobileRecordCard from '../components/common/MobileRecordCard'
import MobileSheet from '../components/common/MobileSheet'
import ViewHeader from '../components/ViewHeader'
import KpiCardsSkeleton from '../Skeletons/KpiCardsSkeleton'
import TableSkeleton from '../Skeletons/TableSkeleton'
import { FS, SP, TOUCH_TARGET_COARSE_QUADRAT } from '../theme/tokens'
import { STATUS_LABELS } from './constants'
import CaseTime from '../components/common/CaseTime'
import useListQuery from '../components/common/listQuery/useListQuery'
import { SLA_KIND_LABELS } from './timeline/toActivityEntry'
// 🔴 R16-R3-C1 (Nachbesserung 5): die Textstimme der Karten-`meta`-Zeile kommt
// aus `RMAFaelligkeit.jsx` — dieselbe Konstante, nicht eine zweite Abschrift.
import { KARTE_TEXT_SX } from './RMAFaelligkeit'

const API = process.env.REACT_APP_API_BASE_URL

/** Die Seitengrösse der Failsafe-Liste (C5-8, N9.10). */
const ROWS_PER_PAGE = 50

/** „unbekannt ist nicht 0" (Regel 22, Komponentenregel 3) — EIN Literal. */
const UNBEKANNT = '—'

const kindLabel = (type) => SLA_KIND_LABELS[type] || String(type || '').replace(/_/g, ' ')

// 🔴 R16-W2 (Nachbesserung 2): das Hausetikett, nicht der Rohwert. `STATUS_LABELS`
// ist ein Proxy mit `humanizeStatus`-Rückfall, deckt also auch einen Zombie-Status
// („with customer" → „With customer"). Bis hierher stand hier ein eigenes
// `replace(/_/g, ' ')` und die Seite war damit die ZWEITE Form neben der
// Musterroute (`design/muster/ListenMuster.jsx`), die `STATUS_LABEL` rendert.
const statusLabel = (s) => STATUS_LABELS[s]

// N65 (2026-09-28, plan §F2.6, E20): label and colour per case type from ONE
// map. Since N65 the inquiry carries SLA rows (`agreed`, `awaiting_shipment`),
// and the old two-way ternary labelled it "Return". An unknown case type shows
// its raw word, never "Return" (no silent else). The chip carries text, not
// only colour (rule 22).
//
// 🔴 R16 r2 C2: the inquiry's `default` is NOT "there is no third meaning
// colour" - that sentence was wrong, the app HAS an inquiry colour
// (`secondary`, in CaseTypeBadge / CaseTypeIcon / WIZARD_FALLART_CHIP). The real
// reasons, measured after WCAG 1.4.3 (MUI 7.3.5, outlined chip: the text colour
// is `palette[color].main`, 11,5 px at weight 600, so the target is 4,5:1):
//   `default`  16,1:1 on white
//   `secondary` 2,15:1 - would FAIL the contrast rule in this chip
//   `info`      3,86:1, `warning` 3,11:1 - both also below 4,5:1, carried as
//               pre-existing debt (see the Akte below)
//   `secondary` vs `warning` is 1,45:1, so inquiry and return would be nearly
//               indistinguishable in this table
// So `default` is a deliberate deviation from CaseTypeBadge, taken for contrast
// and for distance from `warning`. CaseTypeBadge's own `else` additionally says
// "Service" silently - a second reason not to copy it here.
// Backlog: docs/backlog-auto-repair/2026-09-29-fallart-chips-kontrast.md.
const TYPE_CHIPS = {
  service: { label: 'Service', color: 'info' },
  return: { label: 'Return', color: 'warning' },
  inquiry: { label: 'Inquiry', color: 'default' },
}

function TypeChip({ caseType }) {
  const chip = TYPE_CHIPS[caseType] || { label: String(caseType || 'unknown'), color: 'default' }
  return (
    <Chip
      size="small"
      variant="outlined"
      color={chip.color}
      label={chip.label}
      sx={{ fontSize: FS.xs, height: 20, fontWeight: 600 }}
    />
  )
}

/**
 * Ein Listenkopf: Titel links, Zähler rechts. 🔴 Eine Zahl je Liste (W4/P8) —
 * die drei Listen tragen je genau EINEN `ResultCount`, und der Zähler steht
 * NEBEN dem Titel, nicht darin (AZ3: keine Zählung im Titel).
 */
function ListenKopf({ titel, farbe, zaehler }) {
  return (
    <Stack direction="row" spacing={SP.sm} alignItems="baseline" justifyContent="space-between" sx={{ mb: SP.sm }}>
      <Typography variant="subtitle2" color={farbe || 'text.primary'}>{titel}</Typography>
      {zaehler}
    </Stack>
  )
}

/**
 * Der Sortiersatz (P18) — EINE Marke, die die Probe liest.
 *
 * 🔴 R15-Nit 2 (Nachbesserung 2): die Marke stand ZWEIMAL auf der Seite
 * (`sortiert-nach` in der Live-Liste und im Katalog). `messeP18` liest per
 * `document.querySelector` die erste — heute richtig, aber ein künftiger
 * strenger Locator (`getByTestId`) bräche daran. Die Live-Liste behält den
 * Hausnamen (P18 misst sie); der Katalog trägt einen eigenen, damit die Marke
 * eindeutig ist.
 *
 * 🔴 R15-Nit 1 (Nachbesserung 3): `sortiert-nach-katalog` weicht damit von der
 * Hausmarke `sortiert-nach` ab, die sieben weitere Seiten tragen. Wer später
 * eine generische Probe „jede sortierte Liste trägt die Marke" baut, findet den
 * Katalog NICHT — er muss beide Namen kennen (oder die Marke wieder eindeutig
 * machen, dann aber für P18). Die Abweichung ist der Preis der Eindeutigkeit.
 */
function Sortiersatz({ children, testid = 'sortiert-nach' }) {
  return (
    <Typography variant="caption" color="text.secondary" data-testid={testid} sx={{ display: 'block', mb: SP.sm }}>
      {children}
    </Typography>
  )
}

// ── Catalog table: the 18 SLA definitions (14 until N60, 16 until N65) ───────
function CatalogTable({ definitions, error, loading, onRetry }) {
  // C5-7: sortiert nach Fallart, Status, SLA-Typ — clientseitig, nur die
  // Anzeige (C5-9). Der Satz darunter sagt es; ohne die Sortierung wäre der
  // Satz eine Behauptung.
  const sortiert = useMemo(() => {
    const rows = [...(definitions || [])]
    return rows.sort((a, b) => (
      String(a.case_type).localeCompare(String(b.case_type))
      || String(a.status).localeCompare(String(b.status))
      || String(a.type).localeCompare(String(b.type))
    ))
  }, [definitions])

  return (
    <Panel data-testid="liste-panel-katalog">
      <ListenKopf
        titel="SLA catalog"
        zaehler={<ResultCount noun="definitions" n={error ? null : (definitions ? sortiert.length : null)} loading={loading} />}
      />
      {error ? (
        <EmptyState
          reason="error"
          message="Could not load the SLA catalog."
          detail="The live status above comes from a different source and is unaffected."
          onRetry={onRetry}
        />
      ) : loading && !definitions ? (
        // 🔴 B1 (Regel 15/16, Nachbesserung 2): der Katalog hatte KEINEN
        // Ladezustand. Antwortet die Übersicht zuerst, stand hier eine leere
        // Tabelle ohne Meldung — „nichts da" und „noch nicht da" sahen gleich
        // aus. Der Satz „Sorted by …" erscheint erst mit Zeilen; vorher wäre er
        // eine Aussage über eine Liste, die es noch nicht gibt.
        <TableSkeleton framed={false} columns={5} rows={4} ariaLabel="Loading the SLA catalog" />
      ) : sortiert.length === 0 ? (
        // 🔴 R16-W7: der Katalog hatte auch keinen LEERZUSTAND — „0 definitions"
        // über einem leeren Rumpf nennt keinen Grund (Regel 22, Komponentenregel 5).
        <EmptyState
          reason="no-data"
          message="No SLA definitions — the catalog is empty."
        />
      ) : (
        <>
          <Sortiersatz testid="sortiert-nach-katalog">Sorted by case type, status, SLA type.</Sortiersatz>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>SLA type</TableCell>
                  <TableCell align="right">Time limit</TableCell>
                  <TableCell>Reminder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortiert.map((d, i) => (
                  <TableRow key={`${d.case_type}-${d.status}-${d.type}-${i}`} hover>
                    <TableCell><TypeChip caseType={d.case_type} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: FS.sm }}>
                        {statusLabel(d.status)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{kindLabel(d.type)}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{d.hours} h</Typography>
                      <Typography variant="caption" color="text.secondary">{d.days} days</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{d.reminder}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableViewport>
        </>
      )}
    </Panel>
  )
}

// ── Live per-type row (expandable to the overdue cases) ─────────────────────
function OverdueList({ cases }) {
  return (
    <Stack spacing={SP.chip}>
      {cases.map((c) => (
        <Stack key={c.id} direction="row" spacing={SP.sm} alignItems="center" justifyContent="space-between">
          <Link component={RouterLink} to={`/rma/${c.id}`} variant="body2" underline="hover">
            {c.case_number}
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {statusLabel(c.status)}
          </Typography>
          <Typography variant="caption" color="error.main" fontWeight={600}>
            {c.overdue_hours != null ? `${c.overdue_hours} h overdue` : 'overdue'}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

function LiveTypeRow({ row }) {
  const [open, setOpen] = useState(false)
  const hasOverdue = row.overdue_count > 0
  return (
    <>
      {/* 🔴 R16-W6 (Nachbesserung 3): die Trennlinien der Live-Tabelle waren
          Flicken, die des Katalogs durchgezogen. Ursache am Kode: die Zeile nahm
          ALLEN Zellen den Rahmen (`& > *: borderBottom unset`) und verliess sich
          darauf, dass die Aufklapp-Zeile darunter die Linie trägt — deren Zelle
          setzte ihren Rahmen aber nur bei `open`. Damit hing die Linie an ZWEI
          Zuständen (`hasOverdue` × `open`) und war in keiner Kombination die des
          Katalogs: ohne Überfällige fehlte sie ganz, geschlossen ebenso. Jetzt
          trägt jede Zeile genau eine durchgehende Linie — die eigene, oder die
          der Aufklapp-Zelle, die ihren Rahmen immer behält. */}
      <TableRow hover sx={hasOverdue ? { '& > *': { borderBottom: 'unset' } } : undefined}>
        <TableCell padding="checkbox">
          {hasOverdue && (
            // 🔴 R16-W4 (Nachbesserung 2): der Name trug KEINEN Zeilenbezug —
            // bei zehn Zeilen hiessen zehn Knöpfe gleich (BS18). Der mobile
            // Zwilling nennt seit jeher `kindLabel(row.type)`; hier jetzt auch.
            <IconButton
              size="small"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              aria-label={`${open ? 'Collapse' : 'Expand'} ${row.overdue_count} overdue ${kindLabel(row.type)} cases`}
            >
              {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell><TypeChip caseType={row.case_type} /></TableCell>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>{kindLabel(row.type)}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: FS.xs }}>
            {statusLabel(row.status)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2">{row.pending_count}</Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={hasOverdue ? 700 : 400} color={hasOverdue ? 'error.main' : 'text.disabled'}>
            {row.overdue_count}
          </Typography>
        </TableCell>
      </TableRow>
      {hasOverdue && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box py={SP.sm}>
                <OverdueList cases={row.overdue_cases} />
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

/**
 * Die reduzierte Zeile mobil (C5-4) und das Blatt (C5-5).
 *
 * 🔴 R16-B1 (Nachbesserung 3): der Status steht in `meta`, NICHT in `chips`.
 * `chips` trägt allein die Fallart-Pille. Der Grund ist am Bild gemessen
 * (`g13a1c_zweig/390_grund.png`): `MobileRecordCard` setzt im Zeilenzweig
 * `chips` UND `actions` auf `flexShrink: 0` — der Titel bekommt den Rest. Mit
 * Fallart-Pille **und** Statustext in `chips` blieb dem Titel so wenig Breite,
 * dass „No customer reply" WORTWEISE auf drei Zeilen brach und die Pille auf der
 * Höhe des mittleren Wortes stand. Die Pille sitzt jetzt in Zeile 1, der Status
 * in Zeile 2; `chips` trägt genau ein Kind.
 *
 * 🔴 Was R16-W1 (Nachbesserung 2) NICHT war, obwohl es hier stand: der
 * `id`-Slot. Dass die Fallart nicht mehr als Kennung erscheint, ist richtig —
 * die Zeile ist ein AGGREGAT über einen SLA-Typ, keine Fallzeile. Falsch war die
 * Begründung, die Doppelung habe „Zeile 1 die Breite gekostet": der Zeilenzweig
 * von `MobileRecordCard` rendert `id` in den ZWEITEN Stack, zusammen mit `meta`;
 * Zeile 1 besteht aus Titel, `chips` und `actions`. Die Enge kam von
 * `chips` + `actions`, nicht von `id`.
 *
 * 🔴 R16-R3-C1 (Nachbesserung 5): die `meta`-Zeile ist EINE `Typography` mit
 * Trenner, in der Textstimme der Karten-`meta` aus `RMAFaelligkeit.jsx`
 * (`KARTE_TEXT_SX`, kein Monospace). Vorher standen hier ZWEI `Typography` mit
 * 4 px Abstand und OHNE Trenner — am Foto las sich „Open Pending 0" wie ein
 * Satz, und der Status war der kleinere der beiden Texte (`FS.xs` gegen
 * `caption`). Monospace für einen Status hat einen Präzedenzfall, aber in der
 * TABELLE (`:316`, `:271`), nicht in der Kartenzeile.
 *
 * 🔴 R16-R3-W1: die Marke für `messeP22` sitzt am STATUS-`span`, nicht mehr am
 * ganzen Block. Sie trug vorher beide Angaben, und `messeP22`
 * (`tests/listen/helfer.ts`) zählt nur Marken mit LEEREM Text — „Pending N"
 * allein hätte die Probe grün gehalten, auch wenn der Status verschwunden wäre
 * (vakuos, dieselbe Klasse wie R1-W9). Jetzt trägt die Marke genau den Status:
 * ohne ihn ist sie leer, und P22 wird rot.
 */
export function LiveTypeCard({ row, onOpenOverdue }) {
  const hasOverdue = row.overdue_count > 0
  const schluessel = `${row.case_type}-${row.type}`
  return (
    <MobileRecordCard
      variant="row"
      title={kindLabel(row.type)}
      chips={<TypeChip caseType={row.case_type} />}
      meta={(
        <Typography variant="caption" color="text.secondary" sx={KARTE_TEXT_SX}>
          <Box component="span" data-testid={`karte-status-${schluessel}`}>{statusLabel(row.status)}</Box>
          {` · Pending ${row.pending_count}`}
        </Typography>
      )}
      actions={(
        <Stack direction="row" spacing={SP.chip} alignItems="center">
          <Typography
            variant="body2"
            fontWeight={hasOverdue ? 700 : 400}
            color={hasOverdue ? 'error.main' : 'text.disabled'}
          >
            {`${row.overdue_count} overdue`}
          </Typography>
          {hasOverdue && (
            // 🔴 R16-B2 / R15-C1 (Nachbesserung 3, gezogen in Nachbesserung 4):
            // Auf dem TELEFON trägt den Hausboden das THEMA, aus ZWEI Quellen —
            // `theme.js` §MuiIconButton setzt unter `down('sm')` `minWidth` und
            // `minHeight`, und `...TOUCH_TARGET_COARSE_QUADRAT_ACTIVE` deckt grobe
            // Zeiger. Dieses `sx` ist dort wirkungslos (gemessen: sein Entfernen
            // macht die Probe B2 mobil NICHT rot, `g13a1_rotb2`). 🔴 R15-R3-W1:
            // der zweite Lauf (`g13a1_rotb2c`, `sx` durch 24 × 24 ersetzt, Probe
            // bleibt grün) wird hier NICHT mehr als Beleg geführt — er sagt etwas
            // über die Kaskade, und die ist ungemessen: nach der Hausregel
            // „gleiche Spezifität, später injiziert" müsste ein `sx` mit
            // `minWidth: 0` das Thema SCHLAGEN und die Probe rot werden. Warum sie
            // es nicht wurde, ist nicht gemessen, also trägt der Lauf nichts.
            // Es ist mit der Zeiger-Regel des Themas
            // (`@media (pointer: coarse)`) deckungsgleich — einen Gewinn darüber
            // hinaus belegt keine Messung. `TOUCH_TARGET_COARSE_QUADRAT` bleibt die
            // Hausform für runde Knöpfe (dieselbe wie an der Checkbox in
            // `MobileRecordCard.jsx`). `messeP14` ist auf dieser Seite vakuos (die
            // Karte ist selbst eine Hülle in Hausbodengröße), deshalb misst
            // `tests/listen/sla-overview.spec.ts` den Chevron selbst. 🔴 Ihr Rotweg
            // entfernt ALLE DREI Quellen; die Probe hält damit den Boden des
            // Themas fest, nicht dieses `sx`.
            <IconButton
              size="small"
              onClick={() => onOpenOverdue(row)}
              aria-label={`Show ${row.overdue_count} overdue ${kindLabel(row.type)} cases`}
              sx={TOUCH_TARGET_COARSE_QUADRAT}
            >
              <KeyboardArrowRightIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      )}
      fieldTestIds={{
        title: `karte-titel-${schluessel}`,
        chips: `karte-fallart-${schluessel}`,
        // 🔴 R16-R3-W1: `meta` trägt die Marke NICHT mehr — sie sitzt am
        // Status-`span` oben (siehe Docstring), sonst hielte „Pending N" die
        // P22-Probe grün, auch ohne Status.
      }}
    />
  )
}

// ── Live table ──────────────────────────────────────────────────────────────
function LiveTable({ overview, error, loading, onRetry, onOpenOverdue }) {
  const theme = useTheme()
  const istMobil = useMediaQuery(theme.breakpoints.down('sm'))

  // C5-3: overdue desc, dann pending desc. Der letzte Vergleich ist nur ein
  // Tiebreak, damit die Reihenfolge zwischen zwei Renders nicht springt.
  const rows = useMemo(() => {
    const liste = [...((overview && overview.by_type) || [])]
    return liste.sort((a, b) => (
      (b.overdue_count || 0) - (a.overdue_count || 0)
      || (b.pending_count || 0) - (a.pending_count || 0)
      || `${a.case_type}-${a.type}`.localeCompare(`${b.case_type}-${b.type}`)
    ))
  }, [overview])

  return (
    <Panel data-testid="liste-panel-live">
      <ListenKopf
        titel="Live status — open cases per SLA type"
        zaehler={<ResultCount noun="SLA types" n={error ? null : (overview ? rows.length : null)} loading={loading} />}
      />
      {error ? (
        <EmptyState
          reason="error"
          message="Could not load the live SLA status."
          // 🔴 R15-W1 (Nachbesserung 2): die Failsafe-Liste hängt an DERSELBEN
          // Quelle und wird mit ihr ausgeblendet — der Fehlertext nannte nur
          // Katalog und Live-Status. Ein Leser sah zwei Flächen und erfuhr
          // nirgends, dass eine dritte fehlt.
          detail="The catalog below comes from a different source and is unaffected. The failsafe list below comes from the same source and is not rendered."
          onRetry={onRetry}
        />
      ) : loading && !overview ? (
        // 🔴 B1 (Regel 15/16, Nachbesserung 2): hier stand die Verzweigung
        // `rows.length === 0` OHNE Ladezustand. Sobald der Katalog antwortete
        // (die billigere Quelle) und die Übersicht noch lief, behauptete diese
        // Fläche „No open SLAs — nothing pending or overdue." — genau die
        // Falschaussage, gegen die C5-6 antritt, nur eine Ebene höher. Derselbe
        // Weg beim Wiederholklick: „Try again" setzt `error` zurück und
        // `loading` hoch, `overview` bleibt `null` — die Alles-in-Ordnung-Meldung
        // ersetzte die Fehlermeldung. Der Leerzustand steht jetzt NUR nach einer
        // Antwort.
        <TableSkeleton framed={false} columns={5} rows={3} ariaLabel="Loading the live SLA status" />
      ) : rows.length === 0 ? (
        <EmptyState
          reason="no-data"
          message="No open SLAs — nothing pending or overdue."
        />
      ) : (
        <>
          <Sortiersatz>Sorted by overdue, then pending.</Sortiersatz>
          {istMobil ? (
            <Box data-testid="liste-karte">
              <Stack spacing={0}>
                {rows.map((row) => (
                  <LiveTypeCard
                    key={`${row.case_type}-${row.type}`}
                    row={row}
                    onOpenOverdue={onOpenOverdue}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <Box data-testid="liste-tabelle">
              <TableViewport>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Type</TableCell>
                      <TableCell>SLA type</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="right">Overdue</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => <LiveTypeRow key={`${row.case_type}-${row.type}`} row={row} />)}
                  </TableBody>
                </Table>
              </TableViewport>
            </Box>
          )}
        </>
      )}
    </Panel>
  )
}

// ── Failsafe section — no-SLA-def cases overdue via sla_due_date ─────────────
/**
 * C5-6: rendert IMMER, sobald die Übersicht geladen ist — mit Fällen die
 * Tabelle, ohne Fälle ein Leerzustand mit Grund. Vorher stand hier
 * `return null`: „kein Befund" und „nicht gemessen" sahen gleich aus.
 *
 * `page`/`onPageChange` sind optional, damit der bestehende Jest-Aufruf
 * (`RMASlaOverview.test.jsx`, nur `overview`) unverändert läuft.
 */
export function FailsafeSection({ overview, page = 1, onPageChange, loading = false }) {
  const cases = (overview && overview.no_sla_overdue) || []
  const seite = Math.min(Math.max(page, 1), Math.max(1, Math.ceil(cases.length / ROWS_PER_PAGE)))
  const sichtbar = cases.slice((seite - 1) * ROWS_PER_PAGE, seite * ROWS_PER_PAGE)

  // 🔴 R16-W5 (Nachbesserung 3): im Ladefenster der Übersicht fehlte die dritte
  // Liste GANZ — die Seite stand mit Live-Gerüst und Katalog da, und mit der
  // Antwort wuchs sie um den Failsafe-Abschnitt, sodass der Katalog nach unten
  // sprang. Der Platzhalter hält den Platz: derselbe Panel-Kopf mit leerem
  // Zähler (R15-R3-Nit 2: `ResultCount` mit `n={null}` sagt NICHTS — er rendert
  // einen LEEREN `role="status"`-Bereich mit `aria-busy`, damit die spätere
  // Änderung angesagt wird; „noch unbekannt" stand hier falsch) plus ein Gerüst derselben
  // Spaltenzahl (4: Case, Type, Status, SLA due date).
  if (loading) {
    return (
      <Panel data-testid="liste-panel-failsafe">
        <ListenKopf
          titel="No SLA definition — failsafe overdue"
          zaehler={<ResultCount noun="cases" n={null} loading />}
        />
        <TableSkeleton framed={false} columns={4} rows={2} ariaLabel="Loading the failsafe list" />
      </Panel>
    )
  }

  return (
    // 🔴 R16-W3 (Nachbesserung 2): Rahmen- und Titelfarbe hingen an NICHTS und
    // malten auch den LEEREN Abschnitt orange — im Leerbild das lauteste Element
    // einer Seite, die „0 / 0" meldet. Die Warnfarbe gilt jetzt nur, wenn etwas
    // drinsteht; die Warnung selbst trägt der `Alert` im Inhalt.
    <Panel
      sx={cases.length > 0 ? { borderColor: 'warning.light' } : undefined}
      data-testid="liste-panel-failsafe"
    >
      <ListenKopf
        titel="No SLA definition — failsafe overdue"
        farbe={cases.length > 0 ? 'warning.main' : undefined}
        zaehler={<ResultCount noun="cases" n={cases.length} />}
      />
      {cases.length === 0 ? (
        <EmptyState
          reason="no-data"
          message="No case outside an SLA definition is overdue."
        />
      ) : (
        <>
          <Alert severity="warning" variant="outlined" sx={{ mb: SP.sm }}>
            These cases sit in a status with no SLA definition (e.g. escalated / approved) and
            are only overdue via the failsafe <code>sla_due_date</code> — real SLA coverage is missing here.
          </Alert>
          <TableViewport>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Case</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">SLA due date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sichtbar.map((c) => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Link component={RouterLink} to={`/rma/${c.id}`} variant="body2" underline="hover">
                        {c.case_number}
                      </Link>
                    </TableCell>
                    <TableCell><TypeChip caseType={c.case_type} /></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: FS.sm }}>
                        {statusLabel(c.status)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="warning.main">
                        {/* Track A5-4b: `sla_due_date` is a `db.Date` column — a
                            DAY, not an instant. A relative reading of a deadline
                            ("in 3 Tagen") is the wrong primary in a due-date column
                            an operator scans for the actual date. */}
                        <CaseTime value={c.sla_due_date} dateOnly variant="absolute" />
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableViewport>
          {/* N8.7: die Fußzeile steht NUR, wenn sie etwas zu sagen hat.
              🔴 R15-W3 (Nachbesserung 2): `TableFooterSimple` zeichnet den Balken
              ab `count > 0`, die PAGINIERUNG aber erst ab `count > rowsPerPage` —
              bei 1…50 Fällen stand hier also ein leerer 44-px-Balken mit Oberkante.
              Der Behälter hängt deshalb an `cases.length > ROWS_PER_PAGE`; erst
              dann gibt es eine Seite zu wechseln. `SEL.fuss` in P3/P8 läuft mit
              einem Stub über einer Seite (60 Fälle). */}
          {cases.length > ROWS_PER_PAGE && (
            <Box data-testid="liste-fuss">
              <TableFooterSimple
                count={cases.length}
                page={seite - 1}
                onPageChange={(p) => onPageChange && onPageChange(p + 1)}
                rowsPerPage={ROWS_PER_PAGE}
                stickyOnMobile={false}
              />
            </Box>
          )}
        </>
      )}
    </Panel>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────
export default function RMASlaOverview() {
  const { state, set } = useListQuery({ pageSize: ROWS_PER_PAGE })

  // C5-2: zwei Quellen, zwei Zustände. Kein gemeinsames `Promise.all` mehr.
  const [definitions, setDefinitions] = useState(null)
  const [defsLoading, setDefsLoading] = useState(true)
  const [defsError, setDefsError] = useState(false)
  const [defsVersuche, setDefsVersuche] = useState(0)

  const [overview, setOverview] = useState(null)
  const [ovLoading, setOvLoading] = useState(true)
  const [ovError, setOvError] = useState(false)
  const [ovVersuche, setOvVersuche] = useState(0)

  const [blatt, setBlatt] = useState(null)

  useEffect(() => {
    let alive = true
    setDefsLoading(true)
    setDefsError(false)
    axios.get(`${API}/api/rma/sla-definitions`)
      .then((r) => { if (alive) setDefinitions(r.data) })
      .catch(() => { if (alive) setDefsError(true) })
      .finally(() => { if (alive) setDefsLoading(false) })
    return () => { alive = false }
  }, [defsVersuche])

  useEffect(() => {
    let alive = true
    setOvLoading(true)
    setOvError(false)
    axios.get(`${API}/api/rma/sla-overview`)
      .then((r) => { if (alive) setOverview(r.data) })
      .catch(() => { if (alive) setOvError(true) })
      .finally(() => { if (alive) setOvLoading(false) })
    return () => { alive = false }
  }, [ovVersuche])

  // Erster Aufbau: BEIDE Quellen unterwegs → Gerüst statt einer halben Seite.
  // Sobald eine geantwortet hat, steht die Fläche (die andere zeigt ihren
  // eigenen Lade-/Fehlerzustand).
  const ersterAufbau = ovLoading && defsLoading && !overview && !definitions

  const pending = ovError || !overview ? null : overview.total_pending
  const overdue = ovError || !overview ? null : overview.total_overdue
  const ohneSla = ovError || !overview ? null : overview.total_no_sla_overdue

  return (
    <PageContainer boxSx={{ display: 'flex', flexDirection: 'column', gap: SP.section }}>
      <ViewHeader
        title="SLA overview"
        breadcrumbs={[{ label: 'After-Sales', href: '/rma' }, { label: 'SLAs' }]}
      />

      {ersterAufbau ? (
        // K-S (UI-Durchgang 2): Seitenaufbau als Gerüst der Kennzahl-Kacheln,
        // nicht als Kreisel. ZWEI Kacheln seit G13a-1 (C5-1) — die Zahl ist die
        // GELADENE, nicht eine runde (`tests/design/skelett-anatomie.spec.ts`).
        <KpiCardsSkeleton cards={2} ariaLabel="Loading SLA overview" />
      ) : (
        <Stack spacing={SP.section}>
          {/* ── KPI-Reihe: ZWEI Kacheln, kein Klickziel (C5-1, P29) ─────── */}
          <Box data-testid="liste-kpi">
            <KpiRow maxMobile={4}>
              {/* 🔴 R15-W2 (Nachbesserung 2): die Kachelzahl war von keiner Probe
                  GEZÄHLT („TWO tiles" stand nur im Testnamen). Jede Kachel trägt
                  eine eigene Marke; der Jest-Fall zählt sie.
                  🔴 R16-W10: `reserveHint` hält die Hinweiszeile schon während des
                  Ladens frei — ohne sie wächst die Kachel beim Eintreffen der Daten
                  um eine Zeile (`StatTile.jsx` beschreibt genau diesen Sprung).
                  🔴 R16-W1 (Nachbesserung 3): EINE Regel für beide Kacheln — die
                  Definition steht IMMER (auch bei „—"), der Fehlertext nur bei
                  Fehler. Vorher gab Pending im Ladefenster gar keinen Hinweis,
                  Overdue seinen — zwei gleiche Kacheln lasen sich verschieden. */}
              <KpiCell>
                <Box data-testid="sla-kpi-kachel">
                  <StatTile
                    label="Pending"
                    value={pending == null ? UNBEKANNT : pending}
                    hint={ovError
                      ? 'Not loaded — the live status did not answer.'
                      : 'open cases, SLA running'}
                    reserveHint
                    valueColor={pending > 0 ? 'warning.main' : undefined}
                  />
                </Box>
              </KpiCell>
              <KpiCell>
                <Box data-testid="sla-kpi-kachel">
                  <StatTile
                    label="Overdue"
                    value={overdue == null ? UNBEKANNT : overdue}
                    hint={ovError
                      ? 'Not loaded — the live status did not answer.'
                      : (ohneSla > 0 ? `incl. ${ohneSla} without SLA def` : 'cases past their SLA deadline')}
                    reserveHint
                    valueColor={overdue > 0 ? 'error.main' : undefined}
                  />
                </Box>
              </KpiCell>
            </KpiRow>
          </Box>

          {/* 🔴 Die ÄUSSERE `liste-panel` umschließt die drei Listen, NICHT die
              KPI-Reihe: `messeP3` verlangt die Hausreihenfolge
              `liste-kpi` → `liste-panel` (`selektoren.js` §ZONEN), und ein
              Panel, das die Kacheln mit umschliesst, stünde ÜBER ihnen. */}
          <Stack spacing={SP.section} data-testid="liste-panel">
            <LiveTable
              overview={overview}
              error={ovError}
              loading={ovLoading}
              onRetry={() => setOvVersuche((n) => n + 1)}
              onOpenOverdue={setBlatt}
            />
            {/* 🔴 R16-W5 (Nachbesserung 3): die dritte Liste steht auch im
                Ladefenster — als Platzhalter. Vorher hing sie an `overview`,
                fehlte also, solange die Quelle lief, und der Katalog sprang beim
                Eintreffen der Antwort nach unten. Bei Fehler bleibt sie weg (der
                Fehlertext der Live-Liste nennt sie ausdrücklich, R15-W1). */}
            {!ovError && (overview || ovLoading) && (
              <FailsafeSection
                overview={overview}
                loading={!overview}
                page={state.page}
                onPageChange={(p) => set('page', p)}
              />
            )}
            <CatalogTable
              definitions={definitions}
              error={defsError}
              loading={defsLoading}
              onRetry={() => setDefsVersuche((n) => n + 1)}
            />
          </Stack>
        </Stack>
      )}

      {/* C5-5: das Detail mobil ist ein Blatt, kein Aufklapper. `MobileSheet`
          und nicht `ActionSheet`: jenes erzwingt immer einen Pflicht-CTA. */}
      <MobileSheet open={Boolean(blatt)} onClose={() => setBlatt(null)}>
        <DialogTitle>
          {blatt ? `${kindLabel(blatt.type)} — ${blatt.overdue_count} overdue` : ''}
        </DialogTitle>
        <DialogContent>
          {blatt && <OverdueList cases={blatt.overdue_cases || []} />}
        </DialogContent>
      </MobileSheet>
    </PageContainer>
  )
}
